import React from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import Select from "react-select";

import MediaAPI from "../../../../api/MediaAPI";
import { getSelectClassNames } from "../../../../components/form/Select";
import Spinner from "../../../../components/utils/Spinner";

import type { JSX } from "react";

import type { IndexMedia } from "../../../../api/MediaAPI.types";
import type {
  HighlightParamsFastExternal,
  HighlightParamsLive,
} from "../../../../api/ProjectAPI.types";

interface HighlightExternalFastLiveConfigProps {
  componentParams: HighlightParamsFastExternal | HighlightParamsLive;
  onChange: (
    params:
      | Pick<HighlightParamsFastExternal, "mediaId">
      | Pick<HighlightParamsLive, "mediaId">,
  ) => void;
}

interface MediaOption {
  value: string;
  label: string;
}

function HighlightExternalFastLiveConfig({
  componentParams,
  onChange,
}: HighlightExternalFastLiveConfigProps): JSX.Element {
  const { t } = useTranslation("editor-highlight");

  const getQueryMediaParams = (): {
    type: string;
    external_type?: "stream";
    live_status?: string[];
  } => {
    if (componentParams.contentType === "fast") {
      return {
        type: "fast",
        external_type: "stream",
      };
    }
    if (componentParams.contentType === "live") {
      return {
        type: "live",
        live_status: ["live", "ongoing", "activating", "scheduled"],
      };
    }
    return {
      type: componentParams.contentType,
    };
  };

  const getIndexMediaAvailableListQuery = MediaAPI.generateQuery({
    fn: MediaAPI.getIndexMediaAvailableList,
    params: [getQueryMediaParams()],
  });

  const { isLoading: isIndexMediaListLoading, data: indexMediaList } = useQuery(
    {
      ...getIndexMediaAvailableListQuery,
      select: (response) => response?.data,
    },
  );

  if (isIndexMediaListLoading) {
    return (
      <div className="m-[1.875rem]">
        <Spinner />
      </div>
    );
  }

  const mediaOptionList: MediaOption[] = (indexMediaList ?? []).map(
    (media: IndexMedia) => ({
      value: media.uuid,
      label: media.label,
    }),
  );

  const selectedMedia =
    mediaOptionList.find(
      (option) => option.value === componentParams?.mediaId,
    ) ?? null;

  return (
    <div className="my-[0.625rem] flex flex-col">
      <div className="py-[0.625rem] text-[0.9375rem] font-medium">
        {t("mediaListAvalaible")}
      </div>
      <div className="w-[350px] max-md:w-full">
        <Select<MediaOption>
          unstyled
          classNames={getSelectClassNames()}
          inputId="select-index-media-highlight"
          isLoading={isIndexMediaListLoading}
          options={mediaOptionList}
          placeholder={t("selectMedia")}
          styles={{ control: () => ({}) }}
          value={selectedMedia}
          onChange={(selected) => {
            onChange({ mediaId: selected?.value ?? null });
          }}
        />
      </div>
    </div>
  );
}
export default HighlightExternalFastLiveConfig;
